import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Label } from '@wds/label';
import { defaultBaseline, defaultFonts, uniqueId, safeRest } from '@wds/utils';
import { forwardRef, useMemo } from 'react';
import { InputMessage } from '@wds/input-message';
import { createMemoStyles, getColor } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStyles = createMemoStyles(function (theme) {
  var spacing = theme.spacing,
    shadow = theme.shadow,
    palette = theme.palette,
    type = theme.type,
    radius = theme.radius;
  var input = {
    height: {
      sm: spacing[6],
      md: 40,
      lg: spacing[7]
    },
    padding: {
      sm: spacing[2],
      md: spacing[4],
      lg: spacing[4]
    }
  };
  var inputStyles = {
    wrenchInput: __assign(__assign(__assign({}, defaultBaseline), defaultFonts(theme)), {
      outline: 'none',
      appearance: 'none',
      boxShadow: 'none',
      color: palette.text.dark,
      border: function (_a) {
        var color = _a.color;
        return [[1, 'solid', getColor(palette, color, {
          fallback: palette.border.main
        })]];
      },
      borderRadius: radius.small,
      backgroundColor: palette.background.light,
      lineHeight: 1.5,
      '&:hover': {
        borderColor: function (_a) {
          var color = _a.color;
          return getColor(palette, color, {
            fallback: palette.border.hover
          });
        },
        boxShadow: shadow.sky,
        transition: ['border-color 100ms', 'box-shadow 100ms']
      },
      '&:focus': {
        borderColor: function (_a) {
          var color = _a.color;
          return getColor(palette, color, {
            fallback: palette.border.active
          });
        },
        transition: ['border-color', '100ms']
      },
      padding: function (_a) {
        var size = _a.size;
        return size ? [[0, input.padding[size]]] : null;
      },
      height: function (_a) {
        var size = _a.size;
        return size ? input.height[size] : null;
      },
      width: function (_a) {
        var stretched = _a.stretched;
        return stretched ? '100%' : stretched;
      },
      fontSize: type.fontSize.body,
      pointerEvents: function (_a) {
        var disabled = _a.disabled;
        return disabled ? 'none' : null;
      },
      opacity: function (_a) {
        var disabled = _a.disabled;
        return disabled ? 0.4 : null;
      }
    })
  };
  return inputStyles;
});

var Input = /*#__PURE__*/forwardRef(function (_a, ref) {
  var ariaLabel = _a.ariaLabel,
    defaultValue = _a.defaultValue,
    _b = _a.disabled,
    disabled = _b === void 0 ? false : _b,
    color = _a.color,
    errorMessage = _a.errorMessage,
    propErrorMessageId = _a.errorMessageId,
    id = _a.id,
    inputSize = _a.inputSize,
    label = _a.label,
    name = _a.name,
    onBlur = _a.onBlur,
    onChange = _a.onChange,
    onClick = _a.onClick,
    _c = _a.readOnly,
    readOnly = _c === void 0 ? false : _c,
    _d = _a.size,
    size = _d === void 0 ? 'md' : _d,
    _e = _a.stretched,
    stretched = _e === void 0 ? false : _e,
    _f = _a.type,
    type = _f === void 0 ? 'text' : _f,
    value = _a.value,
    placeholder = _a.placeholder,
    min = _a.min,
    max = _a.max,
    step = _a.step,
    otherProps = __rest(_a, ["ariaLabel", "defaultValue", "disabled", "color", "errorMessage", "errorMessageId", "id", "inputSize", "label", "name", "onBlur", "onChange", "onClick", "readOnly", "size", "stretched", "type", "value", "placeholder", "min", "max", "step"]);
  var wrenchInput = useStyles({
    size: size,
    stretched: stretched,
    color: color,
    disabled: disabled
  }).wrenchInput;
  var inputID = useMemo(function () {
    return id !== null && id !== void 0 ? id : uniqueId('input');
  }, [id]);
  var errorMessageId = useMemo(function () {
    var id;
    if (errorMessage) {
      id = propErrorMessageId !== null && propErrorMessageId !== void 0 ? propErrorMessageId : uniqueId('errorMessage');
    }
    return id;
  }, [errorMessage, propErrorMessageId]);
  var handleChange = function handleChange(event) {
    onChange === null || onChange === void 0 ? void 0 : onChange(event);
  };
  return jsxs(Fragment, {
    children: [label && jsx(Label, {
      htmlFor: inputID,
      children: label
    }), jsx("input", __assign({
      "aria-label": ariaLabel,
      "aria-describedby": errorMessageId,
      className: wrenchInput,
      defaultValue: defaultValue,
      disabled: disabled,
      id: inputID,
      name: name,
      onBlur: onBlur,
      onClick: onClick,
      onChange: handleChange,
      readOnly: readOnly,
      ref: ref,
      size: inputSize,
      type: type,
      value: value,
      placeholder: placeholder,
      min: min,
      max: max,
      step: step
    }, safeRest(otherProps))), errorMessage && jsx(InputMessage, {
      color: color,
      id: errorMessageId,
      children: errorMessage
    })]
  });
});
Input.displayName = 'Input';

export { Input };
